// ScenarioSwitcher.tsx — Cockpit's N-scenario switcher: a horizontal tab bar of scenario
// names + "＋" to add one, and a single "⋯" (kebab) menu acting on the currently-active
// scenario (Umbenennen/Duplizieren/Auf Standard zurücksetzen/Löschen). Replaces an earlier
// version that showed all four of those as always-visible buttons in their own row —
// user feedback called the flow "umständlich"/"nicht intuitiv"; one menu, opened only when
// needed, is the same four actions with far less always-on chrome. CockpitView still edits
// exactly ONE scenario at a time, this is just what picks which one.
//
// Rename is inline: the kebab's "Umbenennen" item turns the ACTIVE tab itself into a text
// input (no separate row) — Enter/blur confirms, Escape cancels. Delete and "Auf Standard
// zurücksetzen" both go through a native window.confirm (user feedback: "natives popup"),
// not an in-page two-step "click again to confirm" chip, which the previous version used.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ScenarioListEntry } from '../lib/storage.js';
import { MoreIcon } from './icons.js';
import { PopoverMenu } from './PopoverMenu.js';

export interface ScenarioSwitcherProps {
  scenarios: ScenarioListEntry[];
  activeId: string;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onRename: (id: string, label: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onReset: (id: string) => void;
}

export function ScenarioSwitcher({
  scenarios,
  activeId,
  onSelect,
  onAdd,
  onRename,
  onDuplicate,
  onDelete,
  onReset,
}: ScenarioSwitcherProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const active = scenarios.find((e) => e.id === activeId) ?? scenarios[0];

  useEffect(() => {
    if (renamingId) renameInputRef.current?.focus();
  }, [renamingId]);

  function select(id: string) {
    setRenamingId(null);
    onSelect(id);
  }

  function startRename(id: string, currentLabel: string) {
    setRenameValue(currentLabel);
    setRenamingId(id);
  }

  function confirmRename() {
    if (renamingId) onRename(renamingId, renameValue);
    setRenamingId(null);
  }

  function handleResetClick(close: () => void) {
    close();
    if (!active) return;
    const ok = window.confirm(
      `„${active.scenario.label}" wirklich auf die Standardwerte zurücksetzen? Alle Änderungen an diesem Szenario gehen verloren — der Name wird ebenfalls auf den Standard zurückgesetzt.`,
    );
    if (ok) onReset(active.id);
  }

  function handleDeleteClick(close: () => void) {
    close();
    if (!active) return;
    const ok = window.confirm(`„${active.scenario.label}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`);
    if (ok) onDelete(active.id);
  }

  return (
    <div class="scenario-switcher">
      <div class="scenario-tabs-row">
        <div class="segmented segmented-scroll scenario-tabs" role="tablist" aria-label="Szenario wählen">
          {scenarios.map((entry) =>
            renamingId === entry.id ? (
              <input
                key={entry.id}
                ref={renameInputRef}
                type="text"
                class="scenario-tab-rename-input"
                value={renameValue}
                aria-label={`Neuer Name für ${entry.scenario.label}`}
                onInput={(e) => setRenameValue((e.currentTarget as HTMLInputElement).value)}
                onBlur={confirmRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmRename();
                  } else if (e.key === 'Escape') {
                    setRenamingId(null);
                  }
                }}
              />
            ) : (
              <button
                key={entry.id}
                type="button"
                class="chip"
                role="tab"
                aria-selected={entry.id === activeId}
                aria-pressed={entry.id === activeId}
                onClick={() => select(entry.id)}
              >
                {entry.scenario.label}
              </button>
            ),
          )}
        </div>
        <button type="button" class="chip scenario-add-chip" onClick={onAdd} aria-label="Neues Szenario hinzufügen" title="Neues Szenario">
          ＋
        </button>
        {active ? (
          <PopoverMenu label={`Aktionen für ${active.scenario.label}`} triggerContent={<MoreIcon />}>
            {(close) => (
              <>
                <button
                  type="button"
                  class="menu-item"
                  role="menuitem"
                  onClick={() => {
                    close();
                    startRename(active.id, active.scenario.label);
                  }}
                >
                  Umbenennen
                </button>
                <button
                  type="button"
                  class="menu-item"
                  role="menuitem"
                  onClick={() => {
                    close();
                    onDuplicate(active.id);
                  }}
                >
                  Duplizieren
                </button>
                <button type="button" class="menu-item" role="menuitem" onClick={() => handleResetClick(close)}>
                  Auf Standard zurücksetzen
                </button>
                {scenarios.length > 1 ? (
                  <button
                    type="button"
                    class="menu-item menu-item-danger"
                    role="menuitem"
                    onClick={() => handleDeleteClick(close)}
                  >
                    Löschen
                  </button>
                ) : null}
              </>
            )}
          </PopoverMenu>
        ) : null}
      </div>
    </div>
  );
}
